// A REPO-ENVIRONMENT FAULT: the repo's own `bin/setup` refuses the same way on
// several boxes at once, so the fault is the repo's (or something every box shares),
// never one box's. See `../task.ts` for the file docblock.
//
// THE EVIDENCE, prod `tesote/tesote.ai`, 2026-09-24:
//
//   - 07:08–07:20Z: every box's `bin/setup` refused on the base `tesote_test` DB over
//     its 64 MB ceiling — 14 setup failures across 6 boxes; tasks spent 3/3 attempts
//     each and ended `failed gate_unrunnable`.
//   - 13:21–13:24Z: quay.io/minio answered 401, so `bin/setup`'s compose pull failed on
//     8 boxes (16 failures, 8 tasks) in two minutes. A human fixed the repo
//     (tesote.ai `3979cc8d`, pushed 13:23:27Z) and then redispatched all 8 by hand.
//
// The box-fault exclusion (#3094, a set since #4874) answers ONE bad box. It cannot
// answer an environment that is identical on every box: each re-pend goes to the next
// box, which fails the same way, and the ladder is spent three boxes at a time.

import { TaskOrigin } from '../../value-objects/task-envelope/origin';

/**
 * HOW MANY DISTINCT BOXES must refuse a repo's setup with ONE signature, inside
 * {@link REPO_ENV_FAULT_WINDOW_MINUTES}, before the repo — not the boxes — is judged
 * faulty.
 *
 * MEASURED, 14 days of prod setup failures to 2026-09-24 (every repo; the box is the
 * `task.assigned` runner that preceded the error, the signature is
 * {@link repoSetupFailure}'s): every cluster that reached 3 boxes was a repo-wide fault
 * — `developerz-ai/ultimate` 09-17 (7 boxes, then 3), `developerz-ai/ui-debugger-mcp`
 * 09-22 (3 boxes, 8 tasks), and the four tesote clusters (3, 3, 6, 8 boxes). No
 * box-local signature (tesote's container-networking refusal, one box, nine days) ever
 * reached 2 boxes inside an hour. 2 would also have fired on nothing false in that
 * sample, but it is one bad box plus one coincidence; 3 is the smallest count the data
 * separates without that coincidence.
 */
export const REPO_ENV_FAULT_MIN_BOXES = 3;

/**
 * HOW MANY DISTINCT SOURCES — a pull request's branch, else a task — must share the
 * signature inside the window as well. A repo's broken `bin/setup` breaks every task that
 * runs it; ONE branch whose own diff breaks setup breaks only itself, on every box it
 * lands on, and three boxes cannot tell the two apart. Two sources can.
 *
 * MEASURED, prod `repo_env_faults`, the 7 days to 2026-09-25 10:30Z: 17 faults opened,
 * all on tesote/tesote.ai, and 16 had ONE task (and one PR) behind every refusal in their
 * window — among them the #2589 stale-branch run (07:22Z, #5017) and tesote.ai #2603, whose
 * conflict resolution left `vendor/docker/docker-compose.yml` malformed: a `pr_babysit`
 * round on it refused 27 times on 12 boxes, re-opening the fault 9 times from 09:06Z,
 * never charged an attempt, and parked the repo's unrelated babysits. The one fault with
 * two sources (09-24 17:31Z, a minio container exit on #2613's and #2603's tasks) still opens.
 */
export const REPO_ENV_FAULT_MIN_SOURCES = 2;

/**
 * Does a window of refusals on one signature fault the REPO? {@link REPO_ENV_FAULT_MIN_BOXES}
 * distinct boxes (not one bad box) and {@link REPO_ENV_FAULT_MIN_SOURCES} distinct sources
 * (not one bad branch). Short of either, the refusal is graded as its box and its task
 * already grade it.
 */
export function repoEnvFaultTrips(window: { boxes: number; sources: number }): boolean {
  return window.boxes >= REPO_ENV_FAULT_MIN_BOXES && window.sources >= REPO_ENV_FAULT_MIN_SOURCES;
}

/**
 * The window the {@link REPO_ENV_FAULT_MIN_BOXES} boxes must fall inside. 30 minutes is
 * `BOX_FAULT_EXCLUSION_MINUTES`: one task spending its whole `gate_unrunnable` ladder
 * spends it on three DISTINCT boxes inside that window (#4874). That task alone is NOT
 * enough evidence ({@link REPO_ENV_FAULT_MIN_SOURCES}): it is equally its own branch. Measured beside it: 15 minutes trips every cluster
 * above at the same failure, except tesote's 06:32→06:50 spread (17 minutes).
 */
export const REPO_ENV_FAULT_WINDOW_MINUTES = 30;

/**
 * How long ONE held row's window lasts (`tasks.hold_until`). The sweep re-arms it every
 * tick while the fault stands and releases it the moment the fault clears, so this is a
 * FAIL-SAFE, not the wait: if the sweep stops, no row is held past it, and a repo that
 * is still broken re-trips the fault on the next three boxes.
 */
export const REPO_ENV_DOWN_HOLD_MS = 2 * 60 * 60 * 1000;

/**
 * How often ONE held task is let through as a PROBE of the repo's setup when nothing
 * else says the environment changed. A push to the default branch probes at once; this
 * is the clock for a fix that lands OUTSIDE the repo (a registry that recovers, a box an
 * operator repaired). One probe per interval, so a repo that stays broken costs one
 * setup run per interval, never a model token: setup runs before the model.
 */
export const REPO_ENV_PROBE_INTERVAL_MS = 10 * 60 * 1000;

/**
 * A probe that has neither passed setup nor failed it this long after release is
 * abandoned and the slot reopened, so a probe the fleet never claims (no box online,
 * a seat cap) cannot hold the slot forever.
 */
export const REPO_ENV_PROBE_STALE_MS = 60 * 60 * 1000;

/** The longest signature kept — one line, enough to recognise, never a dump. */
export const REPO_SETUP_SIGNATURE_MAX = 240;

/** The longest command tail a fault keeps for the human it escalates to. */
export const REPO_SETUP_TAIL_MAX = 1_500;

/**
 * Which origins' lanes run the repo's own `bin/setup` before any work — the rows a
 * repo-environment fault HOLDS. A row whose lane never runs setup cannot fail it and
 * must keep moving: on tesote.ai the 7 days to 2026-09-24 were 1,235 review and
 * review-reply tasks against 55 manual ones, and holding the reviews would have
 * silenced the one lane the owner uses daily.
 *
 * TRUE for the team lane (`apps/runner/src/modes/worker/team/session/run.ts`
 * `setUpRepoBeforeWork`, every origin that edits the repo) and the single-adapter lane
 * (`apps/runner/src/modes/worker/tree-setup.ts`, `pr_babysit` and `devops`). Measured,
 * prod 7 days to 2026-09-24, tasks that emitted a `team_setup` / `run_tree_setup` row
 * that RAN: `manual` 81, `pr_babysit` 16, `gh_issue` 4, `conversation` 1 — and no
 * review, reply or scout task.
 *
 * `setup` is FALSE on purpose: the onboarding pass is what WRITES `bin/setup`, so it is
 * the repair, and holding it behind the fault it could fix would deadlock the repo.
 *
 * A total `Record`, like its siblings in `./origin-traits`: an origin added to the
 * domain fails the build here until someone says which side it is on.
 */
export const ORIGIN_RUNS_REPO_SETUP: Record<TaskOrigin, boolean> = {
  [TaskOrigin.Conversation]: true,
  [TaskOrigin.GhIssue]: true,
  [TaskOrigin.Sentry]: true,
  [TaskOrigin.Linear]: true,
  [TaskOrigin.Manual]: true,
  /** The in-process review brain; no checkout is set up. */
  [TaskOrigin.PrReview]: false,
  /** Replies on threads; no checkout is set up. */
  [TaskOrigin.PrReviewReply]: false,
  /** The fix loop runs `setUpRunTree` before its harness. */
  [TaskOrigin.PrBabysit]: true,
  /** Born `completed`; never claimed. */
  [TaskOrigin.PrBabysitWatch]: false,
  /** Authors a plan; runs no setup. */
  [TaskOrigin.Scout]: false,
  /** The onboarding pass writes `bin/setup` — the repair, never held (see above). */
  [TaskOrigin.Setup]: false,
  /** The single-adapter lane runs `setUpRunTree`. */
  [TaskOrigin.Devops]: true,
  /** One model call (#6759): no checkout, no `bin/setup`. */
  [TaskOrigin.TriageJudge]: false,
  [TaskOrigin.KbFold]: false,
  [TaskOrigin.PlanAuthor]: false,
};

/** The origins {@link ORIGIN_RUNS_REPO_SETUP} holds, as a list for a SQL `in`. */
export const REPO_SETUP_ORIGINS: readonly TaskOrigin[] = Object.entries(ORIGIN_RUNS_REPO_SETUP)
  .filter(([, runs]) => runs)
  .map(([origin]) => origin as TaskOrigin);

/** One setup refusal, read back out of the box's error message. */
export type RepoSetupFailure = {
  /** The normalised identity boxes are compared on — see {@link repoSetupFailure}. */
  readonly signature: string;
  /** How the script ended, verbatim: `exit 1`, `timed out`, `killed by SIGKILL`. */
  readonly how: string;
  /** The script's own tail as the box redacted and capped it, `''` when it printed nothing. */
  readonly tail: string;
};

/**
 * The phrase BOTH setup refusals carry — the team lane's `TeamRepoSetupError` and the
 * single-adapter lane's `RunTreeSetupError` (`apps/runner`), each
 * `the repo's own ./bin/setup failed before any work (<how>)`. The runner's
 * `repo-setup-signature.test.ts` builds both errors and asserts this reads them, so a
 * reworded message goes red there rather than silently turning this detector off.
 */
const SETUP_FAILED = /the repo's own \.\/bin\/setup failed before any work \(([^)]*)\)/;

/**
 * The phrase a box puts in a setup refusal it could NOT prove ran on its base: a borrowed PR
 * tree (the babysit worktree) whose head does not contain the base the follow-up was graded
 * against, or whose containment git could not answer. Such a refusal is evidence about that
 * branch and never about the repo, so {@link repoSetupFailure} reads it as no testimony.
 * Measured 2026-09-25 (tesote/tesote.ai): ONE babysit task on #2589's day-old branch, whose
 * compose still pulled quay.io/minio (401, fixed on `main` the day before), failed on 3 boxes
 * and faulted the whole repo while `main` set up fine. Before the tail, which runs to the end.
 */
export const SETUP_OFF_BASE_MARK = 'setup ran on a PR head not proven to contain its base';

/** Where the tail starts in both messages. */
const TAIL_MARK = "the setup's tail:\n";

/** ANSI SGR / cursor sequences a coloured setup script prints. */
// biome-ignore lint/suspicious/noControlCharactersInRegex: stripping ESC sequences is the point.
const ANSI = /\x1B\[[0-9;?]*[A-Za-z]/g;

/**
 * One line normalised so the SAME refusal on two boxes compares equal: colour stripped,
 * hex runs of 8+ (ids, digests) to `HEX`, then every digit run to `N` (sizes, ports,
 * attempts, durations — `186 MB` on one box is `148 MB` on the next), whitespace
 * collapsed, capped at {@link REPO_SETUP_SIGNATURE_MAX}.
 */
export function normalizeSetupLine(line: string): string {
  return line
    .replace(ANSI, '')
    .replace(/[0-9a-f]{8,}/gi, 'HEX')
    .replace(/[0-9]+/g, 'N')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, REPO_SETUP_SIGNATURE_MAX);
}

/**
 * Read a box's error message as a `bin/setup` refusal, or `null` when it is not one.
 *
 * THE SIGNATURE is `<how> | <last non-empty line of the tail, normalised>`. The LAST
 * line because that is where a setup script prints its verdict: on 2026-09-24 it was
 * `[test-store] the base test database "tesote_test" is N MB, over the N MB ceiling …`
 * on all 6 boxes of the morning incident and `✖ Image pull failed after N attempts.
 * Retry: docker compose -f vendor/docker/docker-compose.yml pull` on all 8 of the
 * afternoon one — while the lines ABOVE it interleave compose progress differently on
 * every box. `how` stays verbatim: exit 1 and exit 2 are different refusals.
 *
 * A refusal carrying {@link SETUP_OFF_BASE_MARK} (ahead of its tail) reads as `null`.
 *
 * Pure and total over any string.
 */
export function repoSetupFailure(message: string): RepoSetupFailure | null {
  const head = SETUP_FAILED.exec(message);
  if (head === null) return null;
  const at = message.indexOf(TAIL_MARK, head.index);
  // Off its base: a fact about the branch, not the repo ({@link SETUP_OFF_BASE_MARK}).
  if (message.slice(0, at < 0 ? undefined : at).includes(SETUP_OFF_BASE_MARK)) return null;
  const how = (head[1] ?? '').trim();
  const tail = at < 0 ? '' : message.slice(at + TAIL_MARK.length);
  const lines = tail
    .replace(ANSI, '')
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');
  const last = lines.at(-1);
  const signature = last === undefined ? how : `${how} | ${normalizeSetupLine(last)}`;
  return { signature, how, tail: tail.slice(-REPO_SETUP_TAIL_MAX) };
}
